import {
  createDefaultNativeStackNavigator,
  createModalScreenOptions,
  createWebModalScreenOptions,
} from 'app/lib/navigation';
import type { PopularItem } from 'app/types/category';
import { ConfirmScreen } from 'app/screens/quick-new/confirm';
import { ThanksScreen } from 'app/screens/quick-new/thanks';
import { ChooseScreen } from 'app/screens/quick-new/choose';
import { useUISize } from 'app/utils/web-helpers';
import { type IntroModalParams, IntroModalScreen } from './intro-modal';
import { BorrowerNextScreen } from './borrower-next';
import { LenderNextScreen } from './lender-next';
import { InterestsScreen } from './interests';
import { QuickInterestsScreen } from './quick-interests';
import { type SharerType } from 'app/types/preferences';
import type { ComponentProps } from 'react';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();

export type Parameters = {
  introModal: IntroModalParams;
  borrowerNext: undefined;
  lenderNext: undefined;
  interests: {
    sharerType?: SharerType.borrower | SharerType.lender;
    skipExplain?: boolean;
    // If user comes from Babillard, we don't want show 'Maybe later' button
    fromBabillard?: boolean;
  };
  quickInterests: {
    fromBabillard?: boolean;
    fromHauteGaspesie?: boolean;
  };
  'quick-new-choose': undefined;
  'quick-new-confirm': { selectedItems: PopularItem[]; fromBabillard?: boolean };
  'quick-new-thanks': { userNowContributor: boolean; listingIds?: string[]; fromBabillard?: boolean } | undefined;
};

export { linking } from './navigator.linking';

const Stack = createDefaultNativeStackNavigator<Parameters>({
  isModal: true,
  withChat: false,
  withInvite: false,
});

export function Navigator() {
  const { isSmallUI } = useUISize();
  return (
    <Stack.Navigator initialRouteName="introModal">
      <Stack.Screen
        component={IntroModalScreen}
        name="introModal"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route, false) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack.Screen
        component={BorrowerNextScreen}
        name="borrowerNext"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route, false) as ComponentProps<typeof Stack.Navigator>;
          }
          return {
            ...createModalScreenOptions(navigation, route),
            headerShown: false,
          };
        }}
      />
      <Stack.Screen
        component={LenderNextScreen}
        name="lenderNext"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route, false) as ComponentProps<typeof Stack.Navigator>;
          }
          return {
            ...createModalScreenOptions(navigation, route),
            headerShown: false,
          };
        }}
      />
      <Stack.Screen
        component={InterestsScreen}
        name="interests"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route, true) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack.Screen
        component={QuickInterestsScreen}
        name="quickInterests"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route, true) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack.Screen
        component={ChooseScreen}
        name="quick-new-choose"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack.Screen
        component={ConfirmScreen}
        name="quick-new-confirm"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return {
            headerShown: true,
            headerShadowVisible: false,
            headerStyle: { backgroundColor: theme.colors['surface.default'] },
          };
        }}
      />
      <Stack.Screen
        component={ThanksScreen}
        name="quick-new-thanks"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route, true) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route, true);
        }}
      />
    </Stack.Navigator>
  );
}
